"use no memo";

import { useState } from "react";
import { BookingStatus } from "@repo/database";
import { DaySchema } from "@repo/validators";
import { useNavigate } from "@tanstack/react-router";
import { format, isSameDay, set } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { useBooking } from "~/hooks/use-booking";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { cn } from "~/lib/utils";
import { useShiftManager } from "~/managers/use-shift-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  useFieldArray,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

interface FormTimeslot {
  id?: string;
  originalId?: string; // Rename from 'id' to 'originalId'
  start: Date;
  temporary: boolean;
  capacity: number;
  spots: number;
  duration: number;
  open: boolean;
  shiftId?: string; // Add shiftId to track which shift the timeslot belongs to
}

export default function DayForm({
  day,
  onClose,
}: {
  day: Date;
  onClose: (force?: boolean) => void;
}) {
  const { i18n, t } = useTranslation("common");

  const { data } = api.shift.getDay.useQuery(day);

  const navigate = useNavigate();

  const { user } = useUser();

  // Track shift capacities
  const [shiftCapacities, setShiftCapacities] = useState<
    Record<string, number>
  >({});

  const formattedShifts = data?.map((d) => ({
    ...d,
    timeslots: d.timeslots.map((t) => ({
      ...t,
      start: toZonedTime(t.start, user.timeZone),
      shiftId: d.id, // Add shiftId to each timeslot
    })),
  }));

  // Initialize shift capacities with default values
  useState(() => {
    if (formattedShifts && formattedShifts.length > 0) {
      const initialCapacities: Record<string, number> = {};
      formattedShifts.forEach((shift) => {
        initialCapacities[shift.id] = shift.capacity;
      });
    }
  });

  const { bookings } = useBooking();

  const bookingsToday = bookings.filter(
    (booking) =>
      isSameDay(booking.startTime, day) &&
      (booking.bookingStatus === BookingStatus.active ||
        booking.bookingStatus === BookingStatus.checked_in),
  );

  const { updateDay } = useShiftManager();

  const { setUnsavedChanges } = useUnsavedChanges();

  const isClosed = formattedShifts?.some((shift) => shift.closed) ?? false;

  // Track modified timeslots using IDs instead of indexes
  const [modifiedTimeslots, setModifiedTimeslots] = useState<Set<string>>(
    new Set(),
  );

  const form = useForm({
    schema: DaySchema(i18n),
    defaultValues: {
      closed: isClosed,
      startDate: day,
      endDate: day,
      timeslots: [],
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose();
    },
  });

  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: "timeslots",
  });

  // Handle capacity change for a shift
  const handleCapacityChange = (shiftId: string, value: string) => {
    const numValue = parseInt(value, 10) || 0;
    setShiftCapacities((prev) => ({
      ...prev,
      [shiftId]: numValue,
    }));

    // Find all timeslots belonging to this shift
    const shiftTimeslots =
      data?.find((shift) => shift.id === shiftId)?.timeslots ?? [];

    // Update all timeslots that belong to this shift
    shiftTimeslots.forEach((timeslot) => {
      const fieldIndex = fields.findIndex(
        (field) => field.originalId === timeslot.id,
      );

      if (fieldIndex !== -1) {
        // Update existing field
        update(fieldIndex, {
          ...fields[fieldIndex],
          capacity: numValue,
        });
      } else {
        // Add new timeslot to form fields if not already added
        const newTimeslot: FormTimeslot = {
          originalId: timeslot.id,
          temporary: timeslot.temporary,
          id: timeslot.id,
          start: set(timeslot.start, {
            date: day.getDate(),
            month: day.getMonth(),
            year: day.getFullYear(),
          }),
          spots: timeslot.spots,
          duration: timeslot.duration,
          capacity: numValue,
          open: !timeslot.closed,
          shiftId: shiftId,
        };
        setModifiedTimeslots((prev) => new Set([...prev, timeslot.id]));
        append(newTimeslot);
      }
    });
  };

  // Handle timeslot changes
  const handleTimeslotChange = (
    id: string,
    field: keyof FormTimeslot,
    value: string | number | boolean,
  ) => {
    const timeslot = data
      ?.flatMap((shift) => shift.timeslots)
      .find((slot) => slot.id === id);

    if (!timeslot) return;

    const fieldIndex = fields.findIndex((field) => field.originalId === id);

    // Find the shift this timeslot belongs to
    const shift = data?.find((s) => s.timeslots.some((t) => t.id === id));
    const shiftId = shift?.id;
    const shiftCapacity = shiftId ? shiftCapacities[shiftId] || 0 : 0;

    // If toggling the switch
    if (field === "open") {
      if (fieldIndex !== -1) {
        // Remove the timeslot if it exists
        remove(fieldIndex);
        const newModified = new Set(modifiedTimeslots);
        newModified.delete(id);
        setModifiedTimeslots(newModified);
      } else if (Boolean(value) !== !timeslot.closed) {
        // Add new timeslot only if the value is different from original
        const newTimeslot: FormTimeslot = {
          originalId: timeslot.id,
          temporary: timeslot.temporary,
          id: timeslot.id,
          start: set(timeslot.start, {
            date: day.getDate(),
            month: day.getMonth(),
            year: day.getFullYear(),
          }),
          spots: timeslot.spots,
          duration: timeslot.duration,
          capacity: timeslot.capacity,
          open: Boolean(value),
          shiftId,
        };
        setModifiedTimeslots((prev) => new Set([...prev, id]));
        append(newTimeslot);
      }
      return;
    }

    // Handle other field changes
    if (fieldIndex === -1) {
      // If not already in form, create a new entry
      const newTimeslot: FormTimeslot = {
        originalId: timeslot.id,
        temporary: timeslot.temporary,
        id: timeslot.id,
        start: set(timeslot.start, {
          date: day.getDate(),
          month: day.getMonth(),
          year: day.getFullYear(),
        }),
        spots: field === "spots" ? Number(value) : timeslot.spots,
        duration: field === "duration" ? Number(value) : timeslot.duration,
        capacity: field === "capacity" ? Number(value) : timeslot.capacity,
        open: !timeslot.closed,
        shiftId,
      };
      setModifiedTimeslots((prev) => new Set([...prev, id]));
      append(newTimeslot);
    } else {
      // If already in form, update only the changed field
      const currentValue = fields[fieldIndex];
      const updatedTimeslot = {
        ...currentValue,
        [field]:
          typeof value === "string" &&
          ["spots", "duration", "capacity"].includes(field)
            ? Number(value)
            : value,
      };
      update(fieldIndex, updatedTimeslot);
    }
  };

  const handleSubmit = form.handleSubmit(async (formData) => {
    const submissionData = {
      ...formData,
      timeslots: fields.map((field) => ({
        ...field,
        id: field.temporary ? field.originalId : uuidv4(),
        spots: field.spots,
        capacity: field.capacity,
        closed: !field.open,
      })),
    };

    const result = await updateDay(submissionData);
    if (result === true) {
      onClose(true);
      form.reset();
    }
  });

  return (
    <>
      {(!formattedShifts || formattedShifts.length === 0) && (
        <div className="absolute z-10 inset-0 bg-black/80 flex flex-col items-center justify-center gap-4">
          <span className="text-lg font-semibold">{t("Day.no_shifts")}</span>
          <Button
            variant="success"
            onClick={() => {
              onClose();
              void navigate({ to: "/settings/bookings/shifts" });
            }}
          >
            {t("Day.create_new_shift")}
          </Button>
        </div>
      )}
      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          id="day-form"
          className="grid grid-cols-2 gap-2"
        >
          <FormField
            control={form.control}
            name="closed"
            render={({ field }) => (
              <FormItem className="col-span-full">
                <Card className="w-full">
                  <CardContent className="flex flex-row justify-between items-center">
                    <FormLabel size="lg">
                      {t("Day.accept_online_reservations")}
                    </FormLabel>
                    <Switch
                      checked={!field.value}
                      onCheckedChange={(value) => {
                        field.onChange(!value);
                      }}
                    />
                  </CardContent>
                </Card>
              </FormItem>
            )}
          />
          {formattedShifts?.map((shift) => (
            <Card key={shift.id} className="col-span-full">
              <CardContent className="flex flex-row justify-between items-cente py-2 px-6">
                <FormLabel size="lg">{`${t("Day.capcacity")} ${shift.serviceName}`}</FormLabel>
                <Input
                  className="w-[100px]"
                  value={shiftCapacities[shift.id]}
                  onChange={(e) =>
                    handleCapacityChange(shift.id, e.target.value)
                  }
                />
              </CardContent>
            </Card>
          ))}

          <Card className="w-full col-span-full">
            <CardContent className="flex flex-col gap-2 p-0 overflow-hidden">
              <div className="px-6 pt-4">
                <FormLabel size="lg">{t("Day.pacing_title")}</FormLabel>
                <FormDescription>{t("Day.pacing_description")}</FormDescription>
              </div>
              <div className="relative flex flex-col gap-2 overflow-auto  small-scrollbar">
                <div className="flex flex-row gap-2 justify-between p-4 sticky top-0 bg-background z-10">
                  <span className="w-[75px] flex text-xs shrink-0 sticky left-0">
                    {t("Day.slot")}
                  </span>
                  <div className="flex flex-row gap-2">
                    <span className="w-[60px] text-xs text-center shrink-0">
                      {t("Day.guests")}
                    </span>
                    <span className="w-[125px] text-xs flex justify-center shrink-0">
                      {t("Day.spots")} / {t("Day.capacity")}
                    </span>
                    <span className="text-xs flex justify-center shrink-0">
                      {t("Day.duration")}
                    </span>
                    <span className="w-[35px] flex justify-center shrink-0"></span>
                  </div>
                </div>
                <ul className="max-h-[200px] flex flex-col gap-2 px-4 relative">
                  {formattedShifts
                    ?.flatMap((shift) =>
                      shift.timeslots.map((timeslot) => ({
                        ...timeslot,
                        shiftId: shift.id,
                      })),
                    )
                    .sort((a, b) => {
                      const timeA =
                        a.start.getHours() * 3600000 +
                        a.start.getMinutes() * 60000 +
                        a.start.getSeconds() * 1000 +
                        a.start.getMilliseconds();
                      const timeB =
                        b.start.getHours() * 3600000 +
                        b.start.getMinutes() * 60000 +
                        b.start.getSeconds() * 1000 +
                        b.start.getMilliseconds();
                      return timeA - timeB;
                    })
                    .map((timeslot) => {
                      const daySlot = set(timeslot.start, {
                        date: day.getDate(),
                        month: day.getMonth(),
                        year: day.getFullYear(),
                      });

                      const guestsToday = bookingsToday
                        .filter(
                          (booking) =>
                            booking.startTime.getTime() === daySlot.getTime(),
                        )
                        .reduce((acc, curr) => acc + curr.groupSize, 0);

                      return (
                        <li
                          className="flex flex-row items-center justify-between gap-2 w-full"
                          key={timeslot.id}
                        >
                          <div className="bg-input w-[75px] h-10 flex justify-center p-2 rounded shrink-0 sticky left-0">
                            <Label size="lg">{format(daySlot, "HH:mm")}</Label>
                          </div>

                          <div className="flex flex-row gap-2 items-center">
                            <div
                              className={cn(
                                "w-[60px] shrink-0 p-1 flex items-center justify-center bg-gray-300 text-black rounded-lg border h-10",
                                guestsToday > 0 && "bg-blue-500 text-white",
                              )}
                            >
                              {guestsToday}
                            </div>
                            <Input
                              defaultValue={timeslot.spots}
                              onChange={(e) =>
                                handleTimeslotChange(
                                  timeslot.id,
                                  "spots",
                                  e.target.value,
                                )
                              }
                              className="max-w-[50px] shrink-0"
                            />
                            <Input
                              defaultValue={
                                shiftCapacities[timeslot.shiftId ?? ""] ??
                                timeslot.capacity
                              }
                              onChange={(e) =>
                                handleTimeslotChange(
                                  timeslot.id,
                                  "capacity",
                                  e.target.value,
                                )
                              }
                              className="max-w-[50px] shrink-0"
                            />
                            <Input
                              defaultValue={timeslot.duration}
                              onChange={(e) =>
                                handleTimeslotChange(
                                  timeslot.id,
                                  "duration",
                                  e.target.value,
                                )
                              }
                              className="max-w-[75px] shrink-0"
                            />
                            <Switch
                              defaultChecked={!timeslot.closed}
                              onCheckedChange={(checked) =>
                                handleTimeslotChange(
                                  timeslot.id,
                                  "open",
                                  checked,
                                )
                              }
                            />
                          </div>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </CardContent>
          </Card>
        </form>
      </Form>
    </>
  );
}
